import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SideDrawer, SkeletonBox } from '@/components';
import { getReconnectRequests, createReconnectRequest } from '../../api';

export default function RequestDrawer({
  integration,
  companyIntegrationId,
  isOpen,
  onClose,
}) {
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState(null);

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  // Query: Fetch reconnect requests from API
  const {
    data: requestsData,
    isLoading: isRequestsLoading,
    isError: isRequestsError,
    refetch: refetchRequests,
  } = useQuery({
    queryKey: ['reconnect-requests', companyId],
    queryFn: () => getReconnectRequests(companyId),
    enabled: !!companyId && isOpen,
  });

  // Mutation: Create a new reconnect request via POST API
  const { mutate: createRequest, isPending: isSubmitting } = useMutation({
    mutationFn: createReconnectRequest,
    onSuccess: () => {
      queryClient.invalidateQueries(['reconnect-requests', companyId]);
      setNotes('');
    },
    onError: (err) => {
      alert(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to create reconnect request.',
      );
    },
  });

  useEffect(() => {
    if (isOpen) {
      setFormError(null);
    } else {
      setFormError(null);
    }
  }, [isOpen]);

  const handleAddRequest = (e) => {
    e.preventDefault();
    if (!notes || !notes.trim()) {
      setFormError('Please fill in this field.');
      return;
    }
    setFormError(null);
    createRequest({
      companyId,
      companyIntegrationId,
      payload: { notes },
    });
  };

  const requestsList = requestsData?.results || [];

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      width="md"
      bodyClassName="p-0 flex flex-col min-h-0"
      header={
        <div className="flex items-center justify-between gap-3 w-full pr-2 py-1">
          <h2 className="text-[18px] font-semibold text-gray-900 truncate">
            {integration?.name || 'WhatsApp Integration'} Requests
          </h2>
          <button
            onClick={onClose}
            className="w-7 h-7 bg-[#4CAF50] rounded flex items-center justify-center text-white hover:bg-[#43A047] transition-colors shrink-0 shadow-sm"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      }
    >
      <div className="flex-grow flex flex-col min-h-0 bg-white">
        <div className="px-6 pt-6 pb-6 shrink-0 space-y-6">
          {/* Add Reconnect Request Form */}
          <div className="mb-6 animate-fadeIn">
            <h3 className="text-[15px] font-medium text-[#4CAF50] mb-3">
              Create Reconnect Request
            </h3>
            <div className="bg-[#F8FAF9] rounded-xl p-4">
              <form onSubmit={handleAddRequest}>
                <div className="space-y-1.5 mb-4">
                  <label className="text-[13px] text-gray-500 font-medium">
                    Notes
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Enter notes for this reconnect request..."
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      if (formError) setFormError(null);
                    }}
                    className={`w-full bg-white rounded-lg px-4 py-3 text-[14px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all resize-none ${
                      formError
                        ? 'border border-red-500 focus:ring-red-500/20'
                        : 'border border-gray-100 focus:border-[#4CAF50]/50 focus:ring-[#4CAF50]/20'
                    }`}
                  />
                  {formError && (
                    <span className="text-red-500 text-[12px] mt-1 block">
                      {formError}
                    </span>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#4CAF50] hover:bg-[#43A047] disabled:opacity-50 text-white text-[13px] font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-sm"
                  >
                    {isSubmitting ? (
                      <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Log Reconnect Request
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Reconnect Requests Logs List Header */}
          <h3 className="text-[15px] font-medium text-[#4CAF50] mb-3">
            Communication Logs
          </h3>

          <div className="bg-[#F8FAF9] rounded-xl p-4">
            <div className="space-y-4">
              {isRequestsLoading ? (
                /* Loading skeletons */
                [...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="flex flex-col p-4 bg-white border border-gray-100 rounded-xl space-y-4"
                  >
                    <div className="flex justify-between items-center border-b border-gray-100 pb-3">
                      <SkeletonBox className="h-4 w-24" />
                      <SkeletonBox
                        className="h-5 w-16"
                        rounded="rounded-full"
                      />
                    </div>
                    <div className="flex gap-3 items-start">
                      <SkeletonBox
                        className="w-8 h-8 shrink-0"
                        rounded="rounded-full"
                      />
                      <div className="space-y-2 w-full mt-1">
                        <SkeletonBox className="h-3 w-20" />
                        <SkeletonBox className="h-4 w-full" />
                      </div>
                    </div>
                  </div>
                ))
              ) : isRequestsError ? (
                /* Error block */
                <div className="text-center p-6 bg-red-50 border border-red-100 rounded-xl space-y-3">
                  <span className="text-[13px] font-medium text-red-600 block">
                    Failed to load request logs
                  </span>
                  <button
                    onClick={() => refetchRequests()}
                    className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-[13px] font-medium py-1.5 px-4 rounded-md transition-colors"
                  >
                    Retry Loading
                  </button>
                </div>
              ) : requestsList.length > 0 ? (
                requestsList.map((item) => {
                  const isCompleted =
                    (item.admin_status || item.status || '').toLowerCase() ===
                    'completed';
                  return (
                    <div
                      key={item.id}
                      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-4"
                    >
                      {/* Top Info */}
                      <div className="flex flex-col gap-2 border-b border-gray-100 pb-3">
                        <div className="flex justify-between items-center text-[13px]">
                          <span className="text-gray-500">Admin Status :</span>
                          <span
                            className={`px-2.5 py-0.5 rounded text-[11px] font-semibold ${
                              isCompleted
                                ? 'bg-[#eef5ef] text-[#4CAF50]'
                                : 'bg-amber-50 text-amber-600'
                            }`}
                          >
                            {item.admin_status_label ||
                              item.admin_status ||
                              item.status ||
                              'Pending'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-[13px]">
                          <span className="text-gray-500">Requested At :</span>
                          <span className="font-medium text-gray-900">
                            {new Date(
                              item.requested_at ||
                                item.created_at ||
                                item.checked_at ||
                                Date.now(),
                            ).toLocaleString('en-US', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </span>
                        </div>
                      </div>

                      {/* Chat / Notes */}
                      <div className="flex flex-col gap-4 pt-1">
                        {/* User Note */}
                        <div className="flex gap-3 items-start">
                          <img
                            src="https://ui-avatars.com/api/?name=User&background=f3f4f6&color=6b7280"
                            alt="User"
                            className="w-8 h-8 rounded-full shrink-0 mt-0.5"
                          />
                          <div className="flex flex-col">
                            <span className="text-[11px] text-gray-400 font-medium">
                              User Note
                            </span>
                            <span className="text-[13px] text-gray-900 font-medium mt-0.5">
                              {item.user_notes ||
                                item.notes ||
                                'No notes provided'}
                            </span>
                          </div>
                        </div>

                        {/* Admin Note */}
                        <div className="flex gap-3 items-start justify-end mt-2">
                          <div className="flex flex-col items-end text-right">
                            <span className="text-[11px] text-gray-400 font-medium">
                              Admin Feedback / Notes
                            </span>
                            <span className="text-[13px] text-gray-900 font-medium mt-0.5">
                              {item.admin_notes || 'No Response From Admin Yet'}
                            </span>
                          </div>
                          <div className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center shrink-0 bg-white shadow-sm overflow-hidden mt-0.5 relative">
                            <img
                              src="/flewa-icon.png"
                              alt="Admin"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden w-full h-full items-center justify-center bg-[#4CAF50]/10 text-[#4CAF50] font-bold text-[10px]">
                              FL
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center p-8 bg-white border border-gray-100 rounded-xl text-gray-500 text-[13px]">
                  No reconnect requests logged.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SideDrawer>
  );
}
